import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./Page_Archive.module.css";
import { useSelector } from "react-redux";

const ALL_STATUSES = ["Closed", "Withdrawn", "Expired"];
const RA_SUB_TYPES = ["Open", "Closed"];
const RFP_SUB_TYPES = ["Intrastate", "Interstate"];
const TC_SUB_TYPES = ["Place Bid", "Initiate"];

const PRODUCT_TYPE_MAP = [
  { name: "Bilateral", always: true },
  { name: "RFP/RFQ", productName: "RFP_RFQ" },
  { name: "Reverse Auction", productName: "RA" },
  { name: "TradeConnect", productName: "TRADE_CONNECT" },
];

const ArchiveHeader = ({
  onBack,
  initialType = "Bilateral",
  initialStatus = "Closed",
  initialRaSubType = "Open",
  initialRfpSubType = "Intrastate",
  onTypeChange,
  onStatusChange,
  onRaSubTypeChange,
  onRfpSubTypeChange,
  initialTcSubType = "Place Bid",
  onTcSubTypeChange,
  types = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  )?.toLowerCase();
  const mapingProduct = useSelector(
    (state) => state?.platformSlice?.userPaymentConfigs,
  );
  const stateLabel = useSelector(
    (state) => state?.persistedReducer?.company?.stateLabel,
  );

  const rfpSubTypes = useMemo(
    () =>
      stateLabel === "OD"
        ? RFP_SUB_TYPES.filter((s) => s !== "Intrastate")
        : RFP_SUB_TYPES,
    [stateLabel],
  );

  const accessibleTypes = useMemo(() => {
    return PRODUCT_TYPE_MAP.filter(
      (t) =>
        t.always ||
        mapingProduct?.find((p) => p?.productName === t.productName),
    ).map((t) => t.name);
  }, [mapingProduct]);

  const effectiveTypes = types ?? accessibleTypes;

  const [type, setType] = useState(params.get("type") || initialType);
  const [status, setStatus] = useState(params.get("status") || initialStatus);
  const [raSubType, setRaSubType] = useState(
    params.get("raSubType") || initialRaSubType,
  );
  const [rfpSubType, setRfpSubType] = useState(
    params.get("rfpSubType") || initialRfpSubType,
  );
  const [tcSubType, setTcSubType] = useState(
    params.get("tcSubType") || initialTcSubType,
  );

  const isTcInitiate = type === "TradeConnect" && tcSubType === "Initiate";

  const STATUSES = (() => {
    if (type === "TradeConnect" && role === "consumer") {
      return isTcInitiate
        ? ["Closed", "Withdrawn", "Expired"]
        : ["Closed", "Expired"];
    }
    if (isTcInitiate) {
      return role === "generator"
        ? ["Closed", "Withdrawn", "Expired"]
        : ["Closed", "Expired"];
    }
    if (role === "generator" && type !== "Bilateral") {
      return ["Closed", "Expired"];
    }
    return ALL_STATUSES;
  })();

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    search.set("type", type);
    search.set("status", status);
    if (type === "RA") {
      search.set("raSubType", raSubType);
    } else {
      search.delete("raSubType");
    }
    if (type === "RFP") {
      search.set("rfpSubType", rfpSubType);
    } else {
      search.delete("rfpSubType");
    }
    if (type === "TradeConnect") {
      search.set("tcSubType", tcSubType);
    } else {
      search.delete("tcSubType");
    }
    const nextSearch = `?${search.toString()}`;
    if (nextSearch !== location.search) {
      navigate(
        { pathname: location.pathname, search: nextSearch },
        { replace: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status, raSubType, rfpSubType, tcSubType]);

  useEffect(() => {
    if (typeof onTypeChange === "function") onTypeChange(type);
  }, [type, onTypeChange]);

  useEffect(() => {
    if (typeof onStatusChange === "function") onStatusChange(status);
  }, [status, onStatusChange]);

  useEffect(() => {
    if (typeof onRaSubTypeChange === "function") onRaSubTypeChange(raSubType);
  }, [raSubType, onRaSubTypeChange]);

  useEffect(() => {
    if (typeof onRfpSubTypeChange === "function")
      onRfpSubTypeChange(rfpSubType);
  }, [rfpSubType, onRfpSubTypeChange]);

  useEffect(() => {
    if (typeof onTcSubTypeChange === "function") onTcSubTypeChange(tcSubType);
  }, [tcSubType, onTcSubTypeChange]);

  useEffect(() => {
    if (!STATUSES.includes(status)) {
      setStatus("Closed");
    }
  }, [type, status, tcSubType, STATUSES]);

  useEffect(() => {
    if (!rfpSubTypes.includes(rfpSubType)) {
      setRfpSubType(rfpSubTypes[0]);
    }
  }, [rfpSubTypes, rfpSubType]);

  const handleBack = () => {
    if (typeof onBack === "function") return onBack();
    navigate(-1);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.left}>
          <button
            aria-label="Back"
            onClick={handleBack}
            className={styles.backButton}
          >
            ← Back
          </button>

          <div className={styles.tabs}>
            {effectiveTypes.map((t) => (
              <button
                key={t}
                onClick={() => setType(t)}
                aria-pressed={t === type}
                className={`${styles.tab} ${t === type ? styles.tabActive : ""}`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.tabs}>
          {STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatus(s)}
              aria-pressed={s === status}
              className={`${styles.tab} ${s === status ? styles.tabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {type === "RA" && (
        <div className={styles.raSubTypeBar}>
          <span className={styles.raSubTypeLabel}>RA Type:</span>
          {RA_SUB_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setRaSubType(s)}
              aria-pressed={s === raSubType}
              className={`${styles.raSubTypeTab} ${s === raSubType ? styles.raSubTypeTabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {type === "RFP" && (
        <div className={styles.raSubTypeBar}>
          <span className={styles.raSubTypeLabel}>RFP Type:</span>
          {rfpSubTypes.map((s) => (
            <button
              key={s}
              onClick={() => setRfpSubType(s)}
              aria-pressed={s === rfpSubType}
              className={`${styles.raSubTypeTab} ${s === rfpSubType ? styles.raSubTypeTabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {type === "TradeConnect" && (
        <div className={styles.raSubTypeBar}>
          {TC_SUB_TYPES.map((s) => (
            <button
              key={s}
              onClick={() => setTcSubType(s)}
              aria-pressed={s === tcSubType}
              className={`${styles.raSubTypeTab} ${s === tcSubType ? styles.raSubTypeTabActive : ""}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

export default ArchiveHeader;
